import dynamic from 'next/dynamic'
import Image from 'next/legacy/image'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import styled from 'styled-components'

import structure from '@/structure'
import { breakpoints, colors, fontWeights } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import { makeMainLayout } from '@/layouts/main'
import AsSeenIn from '@/features/About/as-seen-in'
import ContactUs from '@/features/About/contact-us'
import heroImage from '@/features/About/images/hero@3x.png'
import OurMission from '@/features/About/our-mission'
import Press from '@/features/About/press'
import Testimonials from '@/features/About/testimonials'
import { HeaderTheme } from '@/features/Header'
import MainWrapper from '@/features/Structure/main-wrapper'
import ApplicationDownload from '@/components/application-download'
import Head from '@/components/head'

const CitySelector = dynamic(() => import('@/features/CitySelector'))

const Wrapper = styled(MainWrapper)`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: ${colors.background};
`

const SearchWrapper = styled.div`
  width: 100%;
  @media (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 0 25px;
    margin-top: 110px;
  }
  @media (max-width: ${breakpoints.phoneMax}) {
    margin-top: ${structure.search.heightMobile}px;
  }
`

const TopSection = styled.section`
  display: flex;
  width: 100%;
  min-height: 520px;
  position: relative;
  background-color: ${colors.background};
  margin-top: ${structure.search.height}px;
  z-index: 1;
  overflow: hidden;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    margin-top: 0;
  }
  @media (max-width: ${breakpoints.phoneMax}) {
    margin-top: 10px;
  }
`

const BackgroundWrapper = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  z-index: -1;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    width: calc(100% + 100px);
    margin: 0 -50px;
  }

  > span > img {
    background-repeat: no-repeat;
  }
`

const padding = {
  big: 0.275,
  normal: 0.325,
  small: 0.375,
  horizontal: 0.25
}

const TopContentContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding: 0 calc(100vw * ${padding.horizontal}) calc(100vw * ${padding.big});

  @media (max-width: ${breakpoints.tabletLandscape}) {
    padding-bottom: calc(100vw * ${padding.small});
  }

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 0 125px 320px;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    padding: 0 25px 190px;
  }
`

const TopContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const TopTitle = styled.h1`
  width: 920px;
  font-size: 91px;
  font-family: futura-pt-bold, Futura, sans-serif;
  letter-spacing: -0.17px;
  line-height: 86px;
  text-align: center;
  font-weight: 700;
  margin: 80px 0 30px;

  @media (max-width: ${breakpoints.bigDesktop}) {
    width: 800px;
  }

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    width: 85vw;
    font-size: 74px;
    letter-spacing: -0.14px;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 60px 0 30px;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 57px;
    line-height: 54px;
    letter-spacing: -0.1px;
    text-align: left;
  }
`

const TopDescription = styled.p`
  font-family: 'Open Sans', sans-serif;
  font-size: 24px;
  font-weight: ${fontWeights.medium};
  line-height: 33px;
  letter-spacing: -0.2px;
  text-align: center;
  width: 800px;
  margin-top: 0;

  @media screen and (max-width: ${breakpoints.card}) {
    max-width: 630px;
  }

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    width: 85vw;
    font-size: 21px;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 18px;
    line-height: 24px;
    text-align: left;
  }
`
const CurveStyler = styled.div`
  background-color: ${colors.white};
  width: 100%;
  height: 25px;
  border-radius: 25px 25px 0 0;
  bottom: 0;
  position: absolute;
  box-shadow: 0px 5px 8px 0px rgb(42 39 35 / 12%);

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    border-radius: 10px 10px 0 0;
    height: 10px;
    box-shadow: 0px 3px 12px 0px rgb(42 39 35 / 15%);
  }
`

const Content = styled.div`
  background: ${colors.white};
  display: flex;
  flex-direction: column;
  align-items: center;
`

const MissionWrapper = styled.div`
  margin: 150px 0 170px;

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    margin: 80px 0 100px;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin: 60px 0 80px;
  }
`

const PagePadding = `
  padding-left: 140px;
  padding-right: 140px;

  @media only screen and (max-width: ${breakpoints.desktop}) {
    padding-left: 100px;
    padding-right: 100px;
  }

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    padding-left: 70px;
    padding-right: 70px;
  }

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding-left: 0;
    padding-right: 0;
  }
`

const Padding = styled.section`
  ${PagePadding}
  position: relative;
`

const TestimonialsWrapper = styled(Padding)`
  padding: 0 0 150px;

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 0 0 80px;
  }
`

const GreySection = styled.div`
  width: 100%;
  background-color: ${colors.background};
`

const ContactUsWrapper = styled(Padding)`
  padding: 170px 25px;
  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 100px 25px;
  }
`

const Heading = styled.h2`
  padding: 0;
  margin: 0 0 20px;
  font-family: futura-pt-bold, Futura, sans-serif;
  font-size: 57px;
  letter-spacing: -0.1px;
  font-weight: 700;
  line-height: 54px;
  text-align: center;

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    font-size: 45.5px;
    line-height: 44px;
  }

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    text-align: center;
    font-size: 49px;
  }

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 34px;
    letter-spacing: -0.09px;
    line-height: 34px;
    text-align: left;
    margin-left: 30px;
    margin-bottom: 30px;
  }
`

const TestimonialHeading = styled(Heading)`
  margin: 170px 25px 80px;

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 100px 25px 30px;
  }
`

const AsSeenInHeading = styled(Heading)`
  margin: 170px 0 50px;

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 100px 0 30px;
  }

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    margin: 100px 25px 30px;
  }
`

const ContactUsHeading = styled(Heading)`
  margin: 0 0 80px;

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 0 0 30px;
  }
`

const PressHeading = styled(Heading)`
  font-size: 46px;
  font-weight: 700;
  letter-spacing: -0.14px;
  line-height: 50px;
  margin-bottom: 35px;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 34px;
    margin-bottom: 30px;
    text-align: left;
  }
`

const About = () => {
  const { t } = useTranslation('about')
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  const metaTitle = t(
    'about:About Us | liv.rent - Apartments & Houses for Rent Canada'
  )
  const metaDescription = t(
    `about:Canada's trusted house & apartment rental website. Verified landlords. Pre-screened tenants. Find apartments, houses, townhouses, condos and rooms for rent.`
  )

  return (
    <Wrapper>
      <Head title={metaTitle} description={metaDescription} path="/about" />
      <SearchWrapper>
        {displayMobile && (
          <CitySelector instanceId="about-select-city" id="about-select-city" />
        )}
      </SearchWrapper>
      <TopSection>
        <BackgroundWrapper>
          <Image
            src={heroImage}
            alt=""
            layout="fill"
            objectFit="contain"
            objectPosition="center bottom"
            placeholder="blur"
          />
        </BackgroundWrapper>
        <TopContentContainer>
          <TopContent>
            <TopTitle>
              {t('about:Renting is hard, but it doesn’t have to be.')}
            </TopTitle>
            <TopDescription>
              {t(
                'about:We are a team of renters and landlords who understand the struggles and frustrations that come with the rental process.'
              )}
            </TopDescription>
          </TopContent>
          <CurveStyler />
        </TopContentContainer>
      </TopSection>
      <Content>
        <MissionWrapper>
          <OurMission />
        </MissionWrapper>
        <GreySection>
          <AsSeenInHeading>{t('about:As Seen In')}</AsSeenInHeading>
          <AsSeenIn />
          <PressHeading>{t('about:Press')}</PressHeading>
          <Press />
        </GreySection>
        <TestimonialsWrapper>
          <TestimonialHeading>
            {t('about:See what people are saying.')}
          </TestimonialHeading>
          <Testimonials />
        </TestimonialsWrapper>
        <GreySection>
          <ContactUsWrapper>
            <ContactUsHeading>{t('about:Contact Us')}</ContactUsHeading>
            <ContactUs />
          </ContactUsWrapper>
        </GreySection>
      </Content>
      <ApplicationDownload />
    </Wrapper>
  )
}

export const getStaticProps = async ({ locale }: { locale: string }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['translation', 'about']))
    }
  }
}

const headerConfig = {
  showSearch: false,
  showNavItems: true,
  theme: HeaderTheme.TRANSPARENT
}

About.Layout = makeMainLayout({ suppressRegisterModal: true, headerConfig })

export default About
